import React, { useState, useEffect, useRef } from 'react';
import { Search, X, SlidersHorizontal, MoreHorizontal, ChevronDown } from 'lucide-react';
import { useT } from '../../utils/translate';

// Единый набор элементов шапки страницы: заголовок → поиск → фильтры → вид → «Ещё» → главное действие.

function useOutside(ref, onClose) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [ref, onClose]);
}

// ── Page header shell ────────────────────────────────────────────────────────
export function PageHeader({ icon: Icon, title, count, extra, children, below }) {
  return (
    <div className="shrink-0 bg-white border-b border-surface-200">
      <div className="px-4 md:px-6 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 min-w-0 basis-full sm:basis-auto sm:mr-auto">
          {Icon && <Icon className="w-5 h-5 text-primary-600 shrink-0" />}
          <h1 className="text-lg font-bold text-ink truncate">{title}</h1>
          {count != null && count !== '' && (
            <span className="text-xs font-medium text-ink-tertiary bg-surface-100 px-2 py-0.5 rounded-full whitespace-nowrap">{count}</span>
          )}
          {extra}
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end w-full sm:w-auto">{children}</div>
      </div>
      {below}
    </div>
  );
}

// ── Search ───────────────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary pointer-events-none" />
      <input
        className={`input pl-9 pr-8 !py-1.5 text-sm w-full ${value ? '!bg-primary-50 !border-primary-300' : ''}`}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      {value && (
        <button type="button" aria-label="Clear" onClick={() => onChange('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-tertiary hover:text-ink">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ── Filter popover: кнопка с бейджем + панель с полями + «Сбросить» ───────────
export function FilterPopover({ activeCount = 0, onReset, children, width = 'w-80', label, icon: Icon = SlidersHorizontal }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
          activeCount > 0 || open ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-surface-200 bg-white text-ink-secondary hover:border-surface-300 hover:text-ink'
        }`}
      >
        <Icon className="w-4 h-4" />
        <span className="hidden sm:inline">{label || t('ui.filters')}</span>
        {activeCount > 0 && (
          <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary-600 text-white text-xs font-semibold flex items-center justify-center">{activeCount}</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute right-0 top-full mt-2 z-30 ${width} max-w-[calc(100vw-2rem)] bg-white border border-surface-200 rounded-xl shadow-modal`}>
          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">{children}</div>
          {onReset && (
            <div className="px-4 py-3 border-t border-surface-100 flex justify-between items-center">
              <button type="button" onClick={onReset} disabled={activeCount === 0}
                className="text-sm text-ink-secondary hover:text-ink disabled:opacity-40 disabled:hover:text-ink-secondary">
                {t('ui.reset')}
              </button>
              <button type="button" onClick={() => setOpen(false)} className="btn-primary btn-sm">{t('ui.done')}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink-secondary mb-1.5">{label}</span>
      {children}
    </label>
  );
}

// ── «Ещё» menu ───────────────────────────────────────────────────────────────
// items: [{ icon, label, onClick, active, disabled, hidden, danger, divider }]
export function MoreMenu({ items }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const visible = items.filter(i => !i.hidden);
  if (visible.length === 0) return null;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title={t('ui.more')}
        aria-label={t('ui.more')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className={`flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
          open ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-surface-200 bg-white text-ink-secondary hover:border-surface-300 hover:text-ink'
        }`}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-30 w-56 bg-white border border-surface-200 rounded-xl shadow-modal py-1">
          {visible.map((it, i) => (
            <React.Fragment key={it.label + i}>
              {it.divider && <div className="my-1 border-t border-surface-100" />}
              <button
                type="button"
                disabled={it.disabled}
                onClick={() => { setOpen(false); it.onClick?.(); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors disabled:opacity-50 ${
                  it.danger ? 'text-red-600 hover:bg-red-50' : it.active ? 'text-primary-700 bg-primary-50' : 'text-ink hover:bg-surface-50'
                }`}
              >
                {it.icon && <it.icon className={`w-4 h-4 shrink-0 ${it.danger ? '' : it.active ? 'text-primary-600' : 'text-ink-tertiary'}`} />}
                <span className="flex-1">{it.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Segmented control ────────────────────────────────────────────────────────
// options: [{ value, label, icon, badge }]
export function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-0.5 bg-surface-100 rounded-lg max-w-full overflow-x-auto no-scrollbar">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
            value === o.value ? 'bg-white text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'
          }`}
        >
          {o.icon && <o.icon className="w-3.5 h-3.5 hidden sm:block" />}
          {o.label}
          {o.badge ? <span className="text-xs text-ink-tertiary">{o.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

// ── Active filter chips ──────────────────────────────────────────────────────
// chips: [{ key, label, onRemove }]
export function FilterChips({ chips, onClear }) {
  const t = useT();
  if (!chips.length) return null;
  return (
    <div className="px-4 md:px-6 pb-3 flex flex-wrap items-center gap-2">
      {chips.map(c => (
        <span key={c.key} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-primary-50 border border-primary-200 text-xs font-medium text-primary-700">
          {c.label}
          <button type="button" aria-label="Remove" onClick={c.onRemove} className="p-0.5 rounded-full hover:bg-primary-100"><X className="w-3 h-3" /></button>
        </span>
      ))}
      {chips.length > 1 && onClear && (
        <button type="button" onClick={onClear} className="text-xs text-ink-tertiary hover:text-ink">{t('ui.clearAll')}</button>
      )}
    </div>
  );
}

// Нативный select в едином стиле фильтров
export function FilterSelect({ value, onChange, children, className = '' }) {
  return (
    <div className="relative">
      <select
        className={`w-full pl-3 pr-8 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 appearance-none ${
          value ? 'border-primary-300 text-primary-700 font-medium' : 'border-surface-200 text-ink'
        } ${className}`}
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {children}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-disabled pointer-events-none" />
    </div>
  );
}
